/**
 * Pronto-Stima · Public seller funnel
 *
 * Pubblico, no-auth. Atterra qui chi riceve dall'agente un link tipo:
 *     prontoimmo.it/stima/X7K2ABCD
 *
 * Step 1: Dati immobile (città, mq, stato, anno, ecc.)
 * Step 2: Contatti (nome, telefono) + consenso → unlock stima
 * Step 3: Risultato in fascia, branding agente, CTA "Richiedi consulenza"
 *
 * Il lead viene salvato sull'agente come "valuation_request".
 */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";
import CityAutocomplete from "@/components/CityAutocomplete";
import {
  ArrowRight, CheckCircle, Copy,
  ShieldCheck, Spinner, WhatsappLogo, Sparkle, Lock,
} from "@phosphor-icons/react";

const STATE_OPTS = [
  { v: "nuovo",            l: "Nuovo / Ristrutturato" },
  { v: "ottimo",           l: "Ottimo stato" },
  { v: "buono",            l: "Buono stato" },
  { v: "normale",          l: "Da rinfrescare" },
  { v: "scarso",           l: "Scarso" },
  { v: "da_ristrutturare", l: "Da ristrutturare" },
];
const ZONE_OPTS = [
  { v: "centrale",     l: "Centro" },
  { v: "semicentrale", l: "Semicentrale" },
  { v: "periferica",   l: "Periferia" },
  { v: "extraurbana",  l: "Fuori città" },
];
const TYPE_OPTS = [
  { v: "appartamento", l: "Appartamento" },
  { v: "attico",       l: "Attico" },
  { v: "villa",        l: "Villa / Villetta" },
  { v: "monolocale",   l: "Monolocale" },
  { v: "studio",       l: "Studio" },
  { v: "loft",         l: "Loft" },
  { v: "ufficio",      l: "Ufficio" },
];

/* ============================================================
 *  UnlockTeaser — iter v31 r24
 *  Card countdown 12s sotto la stima anonima per spingere
 *  l'utente a sbloccare la stima dettagliata. Quando il
 *  countdown finisce, il badge diventa "Pronta" + pulsa.
 *  Tap → smooth-scroll alla form contatto già presente.
 * ============================================================ */
function UnlockTeaser({ onUnlock, durationSec = 12 }) {
  const [remaining, setRemaining] = useState(durationSec);
  const ready = remaining <= 0;

  useEffect(() => {
    if (remaining <= 0) return;
    const id = setTimeout(() => setRemaining((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [remaining]);

  const progress = Math.max(0, Math.min(1, 1 - remaining / durationSec));

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-[#7C8B6E]/30 p-5 sm:p-6"
      style={{
        background:
          "linear-gradient(135deg, #F1F4ED 0%, #FAFBF7 70%, #F6F1E8 100%)",
      }}
      data-testid="unlock-teaser"
    >
      {/* Decorative glow */}
      <div
        className="absolute -top-20 -right-16 w-[260px] h-[260px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(124,139,110,0.18) 0%, transparent 65%)",
        }}
        aria-hidden
      />

      <div className="relative flex items-start gap-3 sm:gap-4">
        <div
          className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center shrink-0 transition-all ${
            ready ? "bg-[#7C8B6E] shadow-[0_0_24px_rgba(124,139,110,0.5)]" : "bg-white border border-[#7C8B6E]/25"
          }`}
        >
          {ready ? (
            <Sparkle size={20} weight="fill" className="text-white animate-pulse" />
          ) : (
            <Lock size={18} weight="regular" className="text-[#5A6A4D]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#5A6A4D] mb-1">
            {ready ? "Pronta ora" : `Sbloccabile in ${remaining}s`}
          </div>
          <h3
            className="font-editorial text-[20px] sm:text-[24px] leading-tight text-[#1C1C1A]"
            data-testid="unlock-teaser-title"
          >
            {ready ? "La stima dettagliata è pronta." : "Stima dettagliata in arrivo."}
          </h3>
          <p className="mt-1.5 text-[13px] sm:text-[14px] text-[#3F3D38] leading-snug">
            {/* iter v33 r64 · User: riduci testo, uno scorre non legge. */}
            Fascia stretta, €/mq, zona OMI, comparabili. Ti chiediamo{" "}
            <strong className="text-[#1C1C1A]">solo nome e numero.</strong>
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative mt-4 h-[3px] w-full rounded-full bg-[#1C1C1A]/8 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ease-linear ${
            ready ? "bg-[#7C8B6E]" : "bg-[#7C8B6E]/70"
          }`}
          style={{
            width: `${progress * 100}%`,
            transitionDuration: ready ? "200ms" : "1000ms",
          }}
        />
      </div>

      {/* CTA */}
      <button
        type="button"
        onClick={onUnlock}
        className={`relative mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl py-3 text-[14px] font-semibold transition-all active:scale-[0.99] ${
          ready
            ? "bg-[#1C1C1A] hover:bg-black text-white shadow-[0_18px_40px_-18px_rgba(28,28,26,0.55)]"
            : "bg-white hover:bg-[#FAFAF7] text-[#1C1C1A] border border-[#1C1C1A]/15"
        }`}
        data-testid="unlock-teaser-cta"
      >
        {ready ? "Sblocca ora" : "Lasciami i contatti"}
        <ArrowRight size={15} weight="bold" />
      </button>

      <div className="relative mt-3 flex items-center justify-center gap-1.5 text-[10.5px] text-[#9A968B]">
        <ShieldCheck size={12} weight="regular" />
        <span>GDPR · server in Europa · usato solo dall&apos;agente</span>
      </div>
    </div>
  );
}

// iter v29 r241 — Config dichiarativa dei campi mostrati per ciascun tipo.
// Cambia solo questo dict per aggiungere/rimuovere campi per tipo: l'UI lo
// legge automaticamente. Le chiavi `toggles` corrispondono ai bool di `pf`.
const TYPE_CONFIG = {
  appartamento: {
    rooms_label: "Locali",
    show_floor: true,
    floor_label: "Piano",
    toggles: [
      { k: "has_elevator",    l: "Ascensore" },
      { k: "has_balcony",     l: "Balcone" },
      { k: "has_terrace",     l: "Terrazzo" },
      { k: "has_garage",      l: "Garage / Box" },
      { k: "has_cellar",      l: "Cantina" },
      { k: "has_double_bath", l: "Doppi servizi" },
      { k: "has_ac",          l: "Aria condizionata" },
    ],
    show_view: false,
  },
  attico: {
    rooms_label: "Locali",
    show_floor: true,
    floor_label: "Piano",
    toggles: [
      { k: "has_elevator",    l: "Ascensore" },
      { k: "has_terrace",     l: "Terrazzo" },
      { k: "has_garage",      l: "Garage / Box" },
      { k: "has_cellar",      l: "Cantina" },
      { k: "has_double_bath", l: "Doppi servizi" },
      { k: "has_ac",          l: "Aria condizionata" },
    ],
    show_view: true,
  },
  villa: {
    rooms_label: "Locali totali",
    show_floor: false,
    show_villa_floors: true,
    toggles: [
      { k: "has_garden",      l: "Giardino" },
      { k: "has_pool",        l: "Piscina" },
      { k: "has_garage",      l: "Garage / Box" },
      { k: "has_cellar",      l: "Cantina / Taverna" },
      { k: "has_attic",       l: "Mansarda" },
      { k: "has_double_bath", l: "Doppi servizi" },
      { k: "has_ac",          l: "Aria condizionata" },
    ],
    show_view: true,
  },
  monolocale: {
    show_rooms: false,
    show_floor: true,
    floor_label: "Piano",
    toggles: [
      { k: "has_elevator",   l: "Ascensore" },
      { k: "has_balcony",    l: "Balcone" },
      { k: "has_garage",     l: "Garage / Box" },
      { k: "has_cellar",     l: "Cantina" },
      { k: "is_furnished",   l: "Arredato" },
      { k: "has_ac",         l: "Aria condizionata" },
    ],
    show_view: false,
  },
  studio: {
    show_rooms: false,
    show_floor: true,
    floor_label: "Piano",
    toggles: [
      { k: "has_elevator", l: "Ascensore" },
      { k: "has_balcony",  l: "Balcone" },
      { k: "has_garage",   l: "Garage / Box" },
      { k: "is_furnished", l: "Arredato" },
      { k: "has_ac",       l: "Aria condizionata" },
    ],
    show_view: false,
  },
  loft: {
    rooms_label: "Zone / Ambienti",
    show_floor: true,
    floor_label: "Piano",
    toggles: [
      { k: "has_elevator",    l: "Ascensore" },
      { k: "has_terrace",     l: "Terrazzo" },
      { k: "has_garage",      l: "Garage / Box" },
      { k: "has_attic",       l: "Soppalco / Mansarda" },
      { k: "has_double_bath", l: "Doppi servizi" },
      { k: "has_ac",          l: "Aria condizionata" },
    ],
    show_view: false,
  },
  ufficio: {
    rooms_label: "Vani",
    show_floor: true,
    floor_label: "Piano",
    toggles: [
      { k: "has_elevator",    l: "Ascensore" },
      { k: "has_garage",      l: "Garage / Posto auto" },
      { k: "has_double_bath", l: "Doppi servizi" },
      { k: "has_ac",          l: "Aria condizionata" },
    ],
    show_view: false,
  },
};

const VIEW_OPTS = [
  { v: "",            l: "—" },
  { v: "panoramica",  l: "Panoramica" },
  { v: "aperta",      l: "Aperta" },
  { v: "interna",     l: "Interna / cortile" },
];

function formatEuro(n) {
  if (n == null) return "—";
  return "€ " + Math.round(n).toLocaleString("it-IT");
}

// iter v29 r242 — Riepilogo testuale condivisibile.
// Trasforma il risultato della stima in una frase italiana naturale che
// l'utente può copiare o condividere su WhatsApp. Es:
//   «La tua villa di 250 m² con piscina, giardino e vista panoramica a
//    Milano vale circa €2.640.000 – €3.160.000»
function buildShareableSummary(pf, result) {
  if (!result || !pf) return "";
  const typeLabels = {
    appartamento: { word: "appartamento", gender: "m" },
    attico:       { word: "attico",       gender: "m" },
    villa:        { word: "villa",        gender: "f" },
    monolocale:   { word: "monolocale",   gender: "m" },
    studio:       { word: "studio",       gender: "m" },
    loft:         { word: "loft",         gender: "m" },
    ufficio:      { word: "ufficio",      gender: "m" },
  };
  const t = typeLabels[pf.property_type] || typeLabels.appartamento;
  const article = t.gender === "f" ? "La tua" : "Il tuo";
  const surface = Number(pf.surface_sqm) || 0;
  const city = (pf.city || "").trim();
  const cityCap = city ? city.charAt(0).toUpperCase() + city.slice(1) : "";

  // Costruisci la lista di caratteristiche notevoli per il prompt naturale
  const features = [];
  if (pf.has_pool) features.push("piscina");
  if (pf.has_garden) features.push("giardino");
  if (pf.has_terrace) features.push("terrazzo");
  if (pf.has_balcony && !pf.has_terrace) features.push("balcone");
  if (pf.view === "panoramica") features.push("vista panoramica");
  if (pf.has_attic) features.push("mansarda");
  if (pf.has_elevator && t.gender === "m" && pf.property_type !== "villa" && pf.property_type !== "ufficio")
    features.push("ascensore");
  if (pf.has_garage) features.push("box auto");

  let featuresPart = "";
  if (features.length === 1) featuresPart = ` con ${features[0]}`;
  else if (features.length === 2) featuresPart = ` con ${features[0]} e ${features[1]}`;
  else if (features.length >= 3) {
    const last = features[features.length - 1];
    const rest = features.slice(0, -1).join(", ");
    featuresPart = ` con ${rest} e ${last}`;
  }

  const cityPart = cityCap ? ` a ${cityCap}` : "";
  const surfacePart = surface > 0 ? ` di ${surface} m²` : "";

  const min = formatEuro(result.result.range_min);
  const max = formatEuro(result.result.range_max);

  return `${article} ${t.word}${surfacePart}${featuresPart}${cityPart} vale circa ${min} – ${max}.`;
}

export default function Stima() {
  const { code } = useParams();
  const [agent, setAgent] = useState(null);
  const [loadingAgent, setLoadingAgent] = useState(true);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Property fields
  const [pf, setPf] = useState({
    city: "",
    address: "",
    surface_sqm: "",
    rooms: "",
    state: "buono",
    zone: "semicentrale",
    property_type: "appartamento",
    floor: "",
    has_elevator: false,
    has_garage: false,
    has_terrace: false,
    energy_class: "",
    year_built: "",
    // iter v29 r241 — User M1397: «dentro la stima ci sono varie tipologie:
    // villa, ufficio… i dati richiesti sotto devono cambiare. Villa: piani,
    // piscina, giardino». Campi extra (passati al backend, già supportati
    // da EstimateInput in estimate_value.py).
    has_pool: false,           // villa
    has_garden: false,         // villa
    has_balcony: false,        // appartamento/attico/monolocale
    has_cellar: false,         // tutti
    has_attic: false,          // villa/loft
    has_double_bath: false,    // tutti tranne mono/studio
    has_ac: false,             // tutti
    is_furnished: false,       // monolocale/studio
    view: "",                  // attico/villa: panoramica/aperta/interna
    villa_floors: "",          // numero piani della villa (sostituisce floor)
  });
  // Lead fields — iter v28 r358: contatto OPZIONALE.
  // Il funnel è anonymous-first: la stima si vede subito dopo step 1, senza
  // gate. Il contatto si lascia dopo SOLO se l'utente vuole una perizia di
  // persona. Niente più "5 secondi" che diventano una bugia.
  const [lf, setLf] = useState({
    name: "", phone: "", email: "", consent: false, notes: "",
  });
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);

  // iter v29 r241 — Config dinamica dei campi per il tipo selezionato.
  const typeCfg = TYPE_CONFIG[pf.property_type] || TYPE_CONFIG.appartamento;

  useEffect(() => {
    let alive = true;
    axios.get(`${BACKEND_URL}/api/public/stima/${code}`)
      .then(({ data }) => { if (alive) setAgent(data); })
      .catch(() => {
        if (alive) {
          setAgent(null);
          // iter v33 r100 · Il messaggio di errore è già mostrato nella
          // card centrale (agent === null render path). Il toast era duplice.
        }
      })
      .finally(() => { if (alive) setLoadingAgent(false); });
    return () => { alive = false; };
  }, [code]);

  const canStep1 = pf.city.trim().length >= 2 && Number(pf.surface_sqm) >= 10;
  // iter v28 r358 — Step 2 ora è il RISULTATO (no gate). Niente più canStep2.
  const canSendContact = lf.name.trim().length >= 2 && lf.phone.trim().length >= 5 && lf.consent;

  // iter v28 r358 — Submit dopo lo step 1 calcola SUBITO la stima senza
  // chiedere dati personali. La chiamata POST manda solo i campi proprietà
  // (name/phone/consent sono ora opzionali lato backend).
  const onSeeEstimate = async () => {
    if (!canStep1) return;
    setSubmitting(true);
    try {
      const payload = {
        city: pf.city.trim(),
        address: pf.address.trim() || null,
        surface_sqm: Number(pf.surface_sqm),
        state: pf.state,
        zone: pf.zone,
        property_type: pf.property_type,
        rooms: pf.rooms ? Number(pf.rooms) : null,
        // iter v29 r241 — Per villa il "Piano" diventa "Piani della villa".
        // Mappiamo villa_floors sul campo floor del backend (è comunque un
        // intero piccolo, e il pricing di villa non usa la posizione del piano).
        floor: pf.property_type === "villa"
          ? (pf.villa_floors !== "" ? Number(pf.villa_floors) : null)
          : (pf.floor !== "" ? Number(pf.floor) : null),
        has_elevator: pf.has_elevator,
        has_garage: pf.has_garage,
        has_terrace: pf.has_terrace,
        energy_class: pf.energy_class || null,
        year_built: pf.year_built ? Number(pf.year_built) : null,
        // iter v29 r241 — Campi extra (già supportati da EstimateInput)
        has_pool: pf.has_pool,
        has_garden: pf.has_garden,
        has_balcony: pf.has_balcony,
        has_cellar: pf.has_cellar,
        has_attic: pf.has_attic,
        has_double_bath: pf.has_double_bath,
        has_ac: pf.has_ac,
        is_furnished: pf.is_furnished,
        view: pf.view || null,
        // Anonymous request: niente name/phone/consent qui
        consent: false,
      };
      const { data } = await axios.post(`${BACKEND_URL}/api/public/stima/${code}`, payload);
      setResult(data);
      setStep(3);  // Salta lo step 2 (gate rimosso): vai diretto al risultato.
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel calcolo. Riprova tra poco.");
    } finally {
      setSubmitting(false);
    }
  };

  // iter v28 r358 — Invio contatto facoltativo DOPO aver visto la stima.
  // Riusa il POST con gli stessi campi proprietà + dati contatto. Il backend
  // creerà il lead solo se name+phone+consent sono tutti presenti.
  const onSendContact = async () => {
    if (!canSendContact) return;
    setContactSending(true);
    try {
      const payload = {
        city: pf.city.trim(),
        address: pf.address.trim() || null,
        surface_sqm: Number(pf.surface_sqm),
        state: pf.state,
        zone: pf.zone,
        property_type: pf.property_type,
        rooms: pf.rooms ? Number(pf.rooms) : null,
        floor: pf.property_type === "villa"
          ? (pf.villa_floors !== "" ? Number(pf.villa_floors) : null)
          : (pf.floor !== "" ? Number(pf.floor) : null),
        has_elevator: pf.has_elevator,
        has_garage: pf.has_garage,
        has_terrace: pf.has_terrace,
        energy_class: pf.energy_class || null,
        year_built: pf.year_built ? Number(pf.year_built) : null,
        has_pool: pf.has_pool,
        has_garden: pf.has_garden,
        has_balcony: pf.has_balcony,
        has_cellar: pf.has_cellar,
        has_attic: pf.has_attic,
        has_double_bath: pf.has_double_bath,
        has_ac: pf.has_ac,
        is_furnished: pf.is_furnished,
        view: pf.view || null,
        name: lf.name.trim(),
        phone: lf.phone.trim(),
        email: lf.email.trim() || null,
        consent: lf.consent,
        notes: lf.notes.trim() || null,
      };
      await axios.post(`${BACKEND_URL}/api/public/stima/${code}`, payload);
      setContactSent(true);
      toast.success("Richiesta inviata · ti contatterà entro 24h");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio. Riprova tra poco.");
    } finally {
      setContactSending(false);
    }
  };

  // Legacy onSubmit preservato per retrocompatibilità con eventuali test
  // (non più collegato a bottoni in UI; verrà rimosso definitivamente più avanti).
  const onSubmit = onSeeEstimate;

  if (loadingAgent) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <Spinner size={28} className="animate-spin text-[#7C8B6E]" />
      </div>
    );
  }
  if (!agent) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center p-6">
        <div className="max-w-md text-center bg-white rounded-2xl p-8 border border-[#E5E7E0]" data-testid="stima-invalid-code">
          <div className="text-4xl mb-3">🔒</div>
          <h1 className="font-editorial text-2xl text-[#1C1C1A] mb-2">Link non valido</h1>
          <p className="text-sm text-[#6B6A65] mb-5">
            Il link che hai aperto non corrisponde a nessun agente. Verifica con la persona che te l&apos;ha inviato.
          </p>
          {/* iter v28 r358 — Niente più escape link verso prontoimmo.it.
              L'utente è entrato dal link di un agente, deve restare nel flusso. */}
          <div className="text-[11px] text-[#9A968B]">Pronto · stima rapida</div>
        </div>
      </div>
    );
  }

  const agencyDisplay = agent.agency_name || agent.agent_name || "il tuo agente";
  const agentDisplay = agent.agent_name || agent.agency_name || "il tuo agente";
  const agentPhone = agent.agency_phone;
  // iter v28 r358 — Custom copy dell'agente con fallback ai default.
  const customHeadline = (agent.stima_headline || "").trim();
  const customSubtitle = (agent.stima_subtitle || "").trim();
  const customCtaText  = (agent.stima_cta_text  || "").trim() || "Ti chiamo io";

  // Suddivido la headline custom in "prima parte" e "seconda parte in corsivo
  // verde" sul carattere | (es. "Quanto vale casa tua? | Lo scopri in 30 sec.").
  // Se l'agente non usa | tutto va su una sola riga.
  const headlineParts = customHeadline
    ? customHeadline.split("|").map((s) => s.trim()).filter(Boolean)
    : null;

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="public-stima-page">
      {/* ── Top bar ── */}
      {/* iter v33 r61 · User: «pagina stima piu' carina, nome agenzia
          e foto profilo o logo non iniziali». Nuovo ordine di priorita'
          per il "brand mark" a sinistra:
             1. Logo agenzia (immagine rettangolare)
             2. Foto profilo agente (cerchio)
             3. Monogramma iniziale (fallback ultimo)
          Sotto al nome agente aggiungo la SECONDA riga con il nome
          agenzia (se diverso), cosi' entrambi i branding compaiono
          senza ridondanza. Container piu' arioso con accento verde
          sottile per dare piu' presenza editoriale al banner. */}
      <div className="bg-white border-b border-[#E5E7E0] relative">
        <div
          className="absolute inset-x-0 bottom-0 h-[2px] pointer-events-none"
          style={{ background: "linear-gradient(90deg, transparent 0%, #7C8B6E 50%, transparent 100%)", opacity: 0.35 }}
          aria-hidden
        />
        <div className="max-w-3xl mx-auto px-5 py-4 sm:py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-3.5 min-w-0">
            {agent.agency_logo_url ? (
              <img
                src={agent.agency_logo_url}
                alt={agencyDisplay}
                className="h-10 sm:h-11 max-w-[130px] object-contain flex-shrink-0"
                style={{ filter: "grayscale(100%) contrast(1.15) brightness(0.92)" }}
                data-testid="stima-agency-logo"
              />
            ) : agent.agent_picture ? (
              <img
                src={agent.agent_picture}
                alt={agentDisplay}
                className="w-11 h-11 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0 ring-2 ring-[#7C8B6E]/25"
                data-testid="stima-agent-picture"
              />
            ) : (
              <div
                className="w-11 h-11 sm:w-12 sm:h-12 rounded-full text-white flex items-center justify-center text-base font-bold flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #7C8B6E 0%, #5C6B4E 100%)" }}
                data-testid="stima-agent-initial"
              >
                {agentDisplay.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 leading-tight">
              <div className="text-[9.5px] sm:text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold">A cura di</div>
              <div className="text-[14px] sm:text-[15px] font-semibold text-[#1C1C1A] truncate mt-0.5" data-testid="stima-agent-name">
                {agentDisplay}
              </div>
              {/* Nome agenzia sotto — solo se diverso dal nome agente,
                  altrimenti sarebbe una riga ripetuta. */}
              {agent.agency_name && agent.agency_name.trim() && agent.agency_name.trim().toLowerCase() !== (agent.agent_name || "").trim().toLowerCase() && (
                <div className="text-[11.5px] sm:text-[12px] text-[#6B6A65] truncate mt-0.5" data-testid="stima-agency-name">
                  {agent.agency_name}
                </div>
              )}
            </div>
          </div>
          {/* iter v28 r358 — "powered by Pronto" come testo non cliccabile,
              così non porta fuori dal funnel su prontoimmo.it (l'utente è
              qui per fare la stima, non per esplorare il SaaS).
              iter v28 r433 — User M1345: aggiunto il logo Pronto ufficiale
              (P-casa monocromo) accanto al "powered by" per renderlo
              riconoscibile a colpo d'occhio (l'utente reclamava: «il
              logo non è ancora presente»). */}
          <div className="text-[10px] text-[#9A968B] uppercase tracking-widest hidden sm:flex items-center gap-1.5 shrink-0">
            <span>powered by</span>
            <img
              src="/logo/mark-symbol-256.png"
              alt="Pronto"
              className="h-4 w-4 object-contain"
              style={{ filter: "brightness(0)" }}
            />
            <span className="font-semibold text-[#1C1C1A]">Pronto</span>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-8 sm:py-12">
        {/* ── Hero ── */}
        {step === 1 && (
          <div className="mb-8 relative">
            {/* iter v33 r61 · User: «pagina stima piu' carina». Aggiunto
                un accento decorativo verde molto discreto in alto a
                destra per dare piu' presenza editoriale, senza rumore. */}
            <div
              className="absolute -top-4 -right-8 w-32 h-32 pointer-events-none opacity-[0.35]"
              style={{
                background: "radial-gradient(circle at center, rgba(124,139,110,0.20) 0%, transparent 70%)",
              }}
              aria-hidden
            />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F1F4ED] border border-[#7C8B6E]/25">
                <span className="w-1.5 h-1.5 rounded-full bg-[#7C8B6E]"></span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#5C6B4E] font-bold">Pronto · stima casa</span>
              </div>
              <h1 className="font-editorial text-3xl sm:text-4xl text-[#1C1C1A] mt-3 leading-[1.05]" data-testid="stima-hero-headline">
                {headlineParts && headlineParts.length >= 2 ? (
                  <>
                    {headlineParts[0]}<br/>
                    <span className="italic-accent text-[#7C8B6E]">{headlineParts[1]}</span>
                  </>
                ) : headlineParts && headlineParts.length === 1 ? (
                  headlineParts[0]
                ) : (
                  <>
                    Quanto vale la tua casa?<br/>
                    <span className="italic-accent text-[#7C8B6E]">Lo scopri in 30 secondi.</span>
                  </>
                )}
              </h1>
              <p className="text-[14.5px] text-[#4A4945] mt-4 leading-relaxed" data-testid="stima-hero-subtitle">
                {customSubtitle || (
                  <>
                    Solo 4 dati sulla casa. <strong className="text-[#1C1C1A]">Niente nome, niente telefono</strong> per vedere la stima.<br/>
                    Calcolo basato sui valori OMI ufficiali dell&apos;Agenzia delle Entrate.
                  </>
                )}
              </p>
              <div className="mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-[#E5E7E0] text-[11px] text-[#6B6A65]">
                <ShieldCheck size={13} weight="fill" className="text-[#7C8B6E] shrink-0" />
                <span>Anonimo · nessun dato personale richiesto</span>
              </div>
            </div>
          </div>
        )}

        {/* ── Step indicator ── iter v28 r358: ora sono solo 2 step.
            Step 1 = dati casa · Step 2 = risultato (con eventuale contatto). */}
        <div className="flex items-center gap-2 mb-6 text-[11px]" data-testid="stima-step-indicator">
          {[1, 3].map((n) => (
            <div key={n} className={`flex-1 h-1 rounded-full ${step >= n ? "bg-[#7C8B6E]" : "bg-[#E5E7E0]"}`}></div>
          ))}
        </div>

        {/* ─────────────────────────────── STEP 1: Property data */}
        {step === 1 && (
          <div className="bg-white rounded-2xl border border-[#E5E7E0] p-5 sm:p-6 space-y-4" data-testid="stima-step1">
            <div>
              <label className="text-[12px] uppercase tracking-[0.16em] text-[#7C8B6E] font-bold">Tipo immobile</label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 mt-2">
                {TYPE_OPTS.map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setPf((p) => ({ ...p, property_type: o.v }))}
                    className={`text-[11.5px] py-2 px-2 rounded-lg border transition-colors ${
                      pf.property_type === o.v
                        ? "bg-[#7C8B6E] border-[#7C8B6E] text-white"
                        : "bg-white border-[#E5E7E0] text-[#1C1C1A] hover:border-[#7C8B6E]"
                    }`}
                    data-testid={`stima-type-${o.v}`}
                  >{o.l}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[12px] text-[#6B6A65]">Città *</label>
                <CityAutocomplete
                  value={pf.city}
                  onChange={(v) => setPf((p) => ({ ...p, city: v }))}
                  placeholder="es. Milano"
                  testId="stima-city"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-[12px] text-[#6B6A65]">Superficie (m²) *</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={pf.surface_sqm}
                  onChange={(e) => setPf((p) => ({ ...p, surface_sqm: e.target.value }))}
                  placeholder="es. 95"
                  className="w-full mt-1 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E]"
                  data-testid="stima-sqm"
                />
              </div>
              <div>
                <label className="text-[12px] text-[#6B6A65]">Indirizzo (facoltativo)</label>
                <input
                  type="text"
                  value={pf.address}
                  onChange={(e) => setPf((p) => ({ ...p, address: e.target.value }))}
                  placeholder="es. Via Brera 12"
                  className="w-full mt-1 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E]"
                  data-testid="stima-address"
                />
              </div>
              {/* iter v29 r241 — Campo "Locali" condizionale per tipo
                  (Monolocale/Studio non lo mostrano; Ufficio cambia label
                  in "Vani"; Loft in "Zone / Ambienti"; Villa in "Locali totali"). */}
              {typeCfg.show_rooms !== false && (
                <div>
                  <label className="text-[12px] text-[#6B6A65]">{typeCfg.rooms_label || "Locali"}</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={pf.rooms}
                    onChange={(e) => setPf((p) => ({ ...p, rooms: e.target.value }))}
                    placeholder="es. 3"
                    className="w-full mt-1 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E]"
                    data-testid="stima-rooms"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[12px] text-[#6B6A65]">Stato dell&apos;immobile</label>
                <select
                  value={pf.state}
                  onChange={(e) => setPf((p) => ({ ...p, state: e.target.value }))}
                  className="w-full mt-1 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E] bg-white"
                  data-testid="stima-state"
                >
                  {STATE_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[12px] text-[#6B6A65]">Zona</label>
                <select
                  value={pf.zone}
                  onChange={(e) => setPf((p) => ({ ...p, zone: e.target.value }))}
                  className="w-full mt-1 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E] bg-white"
                  data-testid="stima-zone"
                >
                  {ZONE_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
              {/* iter v29 r241 — "Piano" o "Piani della villa" in base al tipo.
                  Villa: "Piani edificio" (1-4). Altri: "Piano". */}
              {typeCfg.show_villa_floors && (
                <div>
                  <label className="text-[12px] text-[#6B6A65]">Piani della villa</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="1"
                    max="5"
                    value={pf.villa_floors}
                    onChange={(e) => setPf((p) => ({ ...p, villa_floors: e.target.value }))}
                    placeholder="es. 2"
                    className="w-full mt-1 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E]"
                    data-testid="stima-villa-floors"
                  />
                </div>
              )}
              {typeCfg.show_floor && (
                <div>
                  <label className="text-[12px] text-[#6B6A65]">{typeCfg.floor_label || "Piano"}</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={pf.floor}
                    onChange={(e) => setPf((p) => ({ ...p, floor: e.target.value }))}
                    placeholder="es. 3"
                    className="w-full mt-1 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E]"
                    data-testid="stima-floor"
                  />
                </div>
              )}
              <div>
                <label className="text-[12px] text-[#6B6A65]">Anno costruzione</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={pf.year_built}
                  onChange={(e) => setPf((p) => ({ ...p, year_built: e.target.value }))}
                  placeholder="es. 1980"
                  className="w-full mt-1 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E]"
                  data-testid="stima-year"
                />
              </div>
              {/* iter v29 r241 — Campo "Vista" visibile solo per attico/villa
                  (impatta il pricing via VIEW_ADJ in estimate_value.py). */}
              {typeCfg.show_view && (
                <div>
                  <label className="text-[12px] text-[#6B6A65]">Vista</label>
                  <select
                    value={pf.view}
                    onChange={(e) => setPf((p) => ({ ...p, view: e.target.value }))}
                    className="w-full mt-1 px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E] bg-white"
                    data-testid="stima-view"
                  >
                    {VIEW_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
              )}
            </div>

            <div>
              <div className="text-[12px] text-[#6B6A65] mb-2">Caratteristiche</div>
              <div className="flex flex-wrap gap-2">
                {/* iter v29 r241 — Toggles dinamici per tipo (vedi TYPE_CONFIG). */}
                {typeCfg.toggles.map((opt) => (
                  <button
                    key={opt.k}
                    type="button"
                    onClick={() => setPf((p) => ({ ...p, [opt.k]: !p[opt.k] }))}
                    className={`text-[12.5px] px-3.5 py-1.5 rounded-full border transition-colors ${
                      pf[opt.k]
                        ? "bg-[#7C8B6E] border-[#7C8B6E] text-white"
                        : "bg-white border-[#E5E7E0] text-[#1C1C1A] hover:border-[#7C8B6E]"
                    }`}
                    data-testid={`stima-toggle-${opt.k}`}
                  >
                    {pf[opt.k] && "✓ "}{opt.l}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              disabled={!canStep1 || submitting}
              onClick={onSeeEstimate}
              className="w-full mt-2 bg-[#1C1C1A] text-white py-3.5 rounded-xl text-sm font-medium hover:bg-[#7C8B6E] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="stima-step1-next"
            >
              {submitting ? (
                <><Spinner size={16} className="animate-spin" /> Calcolo…</>
              ) : (
                <>Vedi la stima <ArrowRight size={16} weight="bold" /></>
              )}
            </button>
            <div className="text-center text-[10.5px] text-[#9A968B] -mt-1 leading-snug">
              Nessuna registrazione · niente nome o telefono per vedere il risultato
            </div>
          </div>
        )}

        {/* iter v28 r358 — Step 2 (contact gate) RIMOSSO.
            La stima si vede subito dopo lo step 1. Il contatto (opzionale)
            si lascia DOPO, nella card "Vuoi una perizia di persona?". */}

        {/* ─────────────────────────────── STEP 2: Contact gating — RIMOSSO iter v28 r358 */}
        {/* Lo step 2 era un GATE bloccante che richiedeva nome+telefono per vedere
            la stima. Rimosso: ora il funnel è anonymous-first. La sezione contatto
            facoltativa si trova nel risultato (step 3). */}

        {/* ─────────────────────────────── STEP 3: Result */}
        {step === 3 && result && (
          <div className="space-y-4" data-testid="stima-step3">
            <div className="bg-[#1C1C1A] text-white rounded-2xl p-6 sm:p-8 text-center">
              <CheckCircle size={32} weight="fill" className="text-[#A8B89A] mx-auto" />
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#A8B89A] font-bold mt-3">Stima indicativa</div>
              <div className="font-editorial text-3xl sm:text-5xl mt-2 leading-tight" data-testid="stima-range">
                {formatEuro(result.result.range_min)}<br className="sm:hidden"/>
                <span className="text-[#A8B89A] mx-1 sm:mx-2">·</span>
                {formatEuro(result.result.range_max)}
              </div>
              <div className="text-[12px] text-white/70 mt-3">
                Valore mediano stimato: <strong className="text-white">{formatEuro(result.result.estimated)}</strong>
              </div>
              <div className="mt-3 text-[11px] text-white/60">
                {formatEuro(result.result.price_per_sqm_min)} · {formatEuro(result.result.price_per_sqm_max)} per m²
                {" · "}
                <span className="uppercase">confidenza {result.result.confidence}</span>
              </div>
            </div>

            {/* iter v31 r24 — UNLOCK TEASER. Countdown 12s sotto la stima anonima
                per creare urgenza percepita e abbattere l'attrito anonimo→lead.
                Quando il countdown finisce il badge pulsa (verde) e il bottone
                fa smooth-scroll alla form contatto già esistente sotto. */}
            <UnlockTeaser
              onUnlock={() => {
                const el = document.querySelector('[data-testid="stima-contact-name"]');
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "center" });
                  setTimeout(() => el.focus?.(), 350);
                }
              }}
            />

            {/* iter v29 r242 — Riepilogo condivisibile: trasforma i numeri
                in una frase italiana naturale, pronta da copiare o inviare
                su WhatsApp. Aumenta la sharability del risultato. */}
            {(() => {
              const summary = buildShareableSummary(pf, result);
              if (!summary) return null;
              const copyToClipboard = async () => {
                try {
                  await navigator.clipboard.writeText(summary);
                  toast.success("Testo copiato");
                } catch {
                  toast.error("Copia non riuscita, prova a selezionare il testo manualmente.");
                }
              };
              const shareWA = () => {
                const url = `https://wa.me/?text=${encodeURIComponent(summary)}`;
                window.open(url, "_blank");
              };
              return (
                <div
                  className="bg-[#F5F0E6] border border-[#E5E0D2] rounded-2xl p-5 sm:p-6"
                  data-testid="stima-shareable-summary"
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#7C8B6E] font-bold mb-2">
                    Riepilogo da condividere
                  </div>
                  <p
                    className="font-editorial text-[18px] sm:text-[20px] text-[#1C1C1A] leading-snug"
                    data-testid="stima-summary-text"
                  >
                    {summary}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <button
                      type="button"
                      onClick={shareWA}
                      className="flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1FB755] text-white text-[13px] font-medium px-4 py-2 rounded-full transition-colors"
                      data-testid="stima-summary-share-wa"
                    >
                      <WhatsappLogo size={15} weight="fill" /> Condividi su WhatsApp
                    </button>
                    <button
                      type="button"
                      onClick={copyToClipboard}
                      className="flex items-center gap-1.5 bg-white hover:bg-[#F1F4ED] border border-[#E5E7E0] text-[#1C1C1A] text-[13px] font-medium px-4 py-2 rounded-full transition-colors"
                      data-testid="stima-summary-copy"
                    >
                      <Copy size={15} weight="bold" /> Copia testo
                    </button>
                  </div>
                </div>
              );
            })()}

            <div className="bg-white rounded-2xl border border-[#E5E7E0] p-5 sm:p-6">
              <div className="flex items-start gap-3">
                {/* iter v33 r61 · Stesso ordine di priorita' del topbar:
                    logo > foto agente > iniziale. */}
                {agent.agency_logo_url ? (
                  <img
                    src={agent.agency_logo_url}
                    alt=""
                    className="w-12 h-12 object-contain rounded-lg flex-shrink-0"
                    style={{ filter: "grayscale(100%) contrast(1.15) brightness(0.92)" }}
                  />
                ) : agent.agent_picture ? (
                  <img
                    src={agent.agent_picture}
                    alt={agentDisplay}
                    className="w-12 h-12 rounded-full object-cover flex-shrink-0 ring-2 ring-[#7C8B6E]/25"
                  />
                ) : (
                  <div
                    className="w-12 h-12 rounded-full text-white flex items-center justify-center text-base font-bold flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, #7C8B6E 0%, #5C6B4E 100%)" }}
                  >
                    {agentDisplay.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-[#7C8B6E] font-bold">La tua stima dettagliata</div>
                  <div className="text-base font-semibold text-[#1C1C1A] mt-0.5">Lasciati ricontattare da {agencyDisplay}.</div>
                  {/* iter v33 r64 · User: taglia paragrafi lunghi. */}
                  <p className="text-[13px] text-[#6B6A65] mt-1.5 leading-snug">
                    Consulenza gratuita telefonica o di persona · valutazione
                    esatta sulla base della casa tua.
                  </p>
                </div>
              </div>

              {/* iter v28 r358 — Form contatto FACOLTATIVO, mostrato dopo la stima.
                  È la versione "anonymous-first": l'utente decide se compilarlo.
                  Una volta inviato, niente più form (contactSent=true). */}
              {!contactSent ? (
                <div className="mt-4 space-y-2.5" data-testid="stima-contact-block">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <input
                      type="text"
                      value={lf.name}
                      onChange={(e) => setLf((l) => ({ ...l, name: e.target.value }))}
                      placeholder="Nome e cognome"
                      className="w-full px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E]"
                      data-testid="stima-contact-name"
                    />
                    <input
                      type="tel"
                      value={lf.phone}
                      onChange={(e) => setLf((l) => ({ ...l, phone: e.target.value }))}
                      placeholder="Telefono"
                      className="w-full px-3 py-2.5 border border-[#E5E7E0] rounded-lg text-sm focus:outline-none focus:border-[#7C8B6E]"
                      data-testid="stima-contact-phone"
                    />
                  </div>
                  <label className="flex gap-2 items-start cursor-pointer text-[11.5px] text-[#6B6A65] leading-snug pt-1">
                    <input
                      type="checkbox"
                      checked={lf.consent}
                      onChange={(e) => setLf((l) => ({ ...l, consent: e.target.checked }))}
                      className="mt-0.5 accent-[#7C8B6E]"
                      data-testid="stima-contact-consent"
                    />
                    <span>
                      Acconsento al trattamento dei dati per essere ricontattato da <strong>{agencyDisplay}</strong>.
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={onSendContact}
                    disabled={!canSendContact || contactSending}
                    className="w-full bg-[#1C1C1A] text-white py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#7C8B6E] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="stima-contact-send"
                  >
                    {contactSending
                      ? <><Spinner size={14} className="animate-spin" /> Invio…</>
                      : <><CheckCircle size={14} weight="fill" /> {customCtaText}</>}
                  </button>
                </div>
              ) : (
                <div className="mt-4 bg-[#F1F4ED] rounded-xl p-3.5 text-[13px] text-[#7C8B6E]" data-testid="stima-contact-sent">
                  <CheckCircle size={16} weight="fill" className="inline-block mr-1.5 text-[#7C8B6E] -mt-0.5" />
                  Richiesta inviata. <strong>{agencyDisplay}</strong> ti contatterà entro 24h.
                </div>
              )}

              {agentPhone && (
                <a
                  href={`https://wa.me/${agentPhone.replace(/[^\d]/g, "")}?text=${encodeURIComponent(`Ciao ${agent.agent_name || ""}, ho appena fatto la stima online (${agencyDisplay}) e vorrei una valutazione di persona.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 w-full bg-[#25D366] text-white py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-90"
                  data-testid="stima-whatsapp-cta"
                >
                  <WhatsappLogo size={18} weight="fill" /> oppure scrivi su WhatsApp
                </a>
              )}
            </div>

            <div className="text-center text-[11px] text-[#9A968B] leading-relaxed px-2">
              Stima basata su dati OMI ufficiali (Agenzia delle Entrate · 2° sem. 2025) con aggiustamenti AVM.
              Non costituisce perizia tecnica giurata.
            </div>

            {/* iter v28 r358 — "powered by Pronto" come testo non cliccabile: l'utente
                resta nel funnel dell'agente, non viene portato fuori sul SaaS Pronto. */}
            <div className="text-center pt-2">
              <span className="text-[11px] text-[#9A968B]">
                powered by <span className="font-semibold text-[#1C1C1A]">Pronto</span>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
